import type { ComponentType } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { AuthEventsBridge } from '@/auth/auth-events-bridge';
import { RequireAuth } from '@/auth/require-auth';
import { RequireOrgApp, RequireStaff } from '@/auth/require-org-app';
import { RequirePermission, RequirePlatformRole } from '@/auth/require-permission';
import { FullPageSpinner } from '@/components/common/spinner';
import { AppLayout } from '@/components/layout/app-layout';
import { NAV_ITEMS } from '@/components/layout/navigation';
import { PlatformRedirect } from '@/components/layout/platform-redirect';
import { ComingSoonPage } from '@/pages/app/coming-soon-page';
import { RouteErrorPage } from '@/pages/public/route-error-page';
import { OLD_SETTINGS_PATHS } from '@/pages/settings/settings-nav';

/** Loads a page's code only when it is first visited (keeps the first download small). */
function page<M>(load: () => Promise<M>, pick: (module: M) => ComponentType) {
  return async () => ({ Component: pick(await load()) });
}

const comingSoonRoutes: RouteObject[] = NAV_ITEMS.filter((item) => item.comingSoon).map((item) => ({
  path: item.to.slice(1),
  element: <ComingSoonPage item={item} />,
}));

export const routes: RouteObject[] = [
  {
    element: <AuthEventsBridge />,
    HydrateFallback: FullPageSpinner,
    // Every page that fails (also an old version's file after a deploy): a page with a way out.
    errorElement: <RouteErrorPage />,
    children: [
      // Logged out. Paths match the links in Step 3's emails.
      {
        path: '/login',
        lazy: page(
          () => import('@/pages/public/login-page'),
          (m) => m.LoginPage,
        ),
      },
      {
        path: '/signup',
        lazy: page(
          () => import('@/pages/public/signup-page'),
          (m) => m.SignupPage,
        ),
      },
      {
        path: '/forgot-password',
        lazy: page(
          () => import('@/pages/public/forgot-password-page'),
          (m) => m.ForgotPasswordPage,
        ),
      },
      {
        path: '/reset-password',
        lazy: page(
          () => import('@/pages/public/reset-password-page'),
          (m) => m.ResetPasswordPage,
        ),
      },
      {
        path: '/verify-email',
        lazy: page(
          () => import('@/pages/public/verify-email-page'),
          (m) => m.VerifyEmailPage,
        ),
      },
      {
        path: '/invite',
        lazy: page(
          () => import('@/pages/public/accept-invite-page'),
          (m) => m.AcceptInvitePage,
        ),
      },
      // Step 16: a client share link (public, read-only).
      {
        path: '/r/:token',
        lazy: page(
          () => import('@/pages/public/report-page'),
          (m) => m.ReportPage,
        ),
      },
      {
        path: '/u/:token',
        lazy: page(
          () => import('@/pages/public/unsubscribe-page'),
          (m) => m.UnsubscribePage,
        ),
      },
      {
        path: '/suspended',
        lazy: page(
          () => import('@/pages/status/status-pages'),
          (m) => m.SuspendedPage,
        ),
      },
      {
        path: '/account-disabled',
        lazy: page(
          () => import('@/pages/status/status-pages'),
          (m) => m.AccountDisabledPage,
        ),
      },

      // Step 17e: Super Admin, the Super Admin console: its own layout and menu.
      {
        path: '/admin',
        element: (
          <RequireAuth>
            <RequireStaff>
              <AppLayout variant="admin" />
            </RequireStaff>
          </RequireAuth>
        ),
        children: [
          {
            element: <RequirePlatformRole roles={['super_admin', 'support']} />,
            children: [
              {
                index: true,
                lazy: page(
                  () => import('@/pages/admin/overview-page'),
                  (m) => m.AdminOverviewPage,
                ),
              },
              {
                path: 'organisations',
                lazy: page(
                  () => import('@/pages/platform/organisations-page'),
                  (m) => m.OrganisationsPage,
                ),
              },
              {
                path: 'organisations/:tenantId/:tab?',
                lazy: page(
                  () => import('@/pages/platform/organisation-page'),
                  (m) => m.OrganisationPage,
                ),
              },
              {
                path: 'users',
                lazy: page(
                  () => import('@/pages/admin/users-page'),
                  (m) => m.AdminUsersPage,
                ),
              },
              // Their own account (name, password, sessions): inside the panel.
              {
                path: 'account/profile',
                lazy: page(
                  () => import('@/pages/admin/account-pages'),
                  (m) => m.AdminProfilePage,
                ),
              },
              {
                path: 'account/security',
                lazy: page(
                  () => import('@/pages/admin/account-pages'),
                  (m) => m.AdminSecurityPage,
                ),
              },
              {
                path: 'audit-log',
                lazy: page(
                  () => import('@/pages/admin/audit-page'),
                  (m) => m.AdminAuditPage,
                ),
              },
            ],
          },
          {
            element: <RequirePlatformRole roles={['super_admin']} />,
            children: [
              {
                path: 'seeds',
                lazy: page(
                  () => import('@/pages/platform/platform-seeds-page'),
                  (m) => m.PlatformSeedsPage,
                ),
              },
              {
                path: 'warmup',
                lazy: page(
                  () => import('@/pages/platform/platform-warmup-page'),
                  (m) => m.PlatformWarmupPage,
                ),
              },
              {
                path: 'ai',
                lazy: page(
                  () => import('@/pages/platform/platform-ai-page'),
                  (m) => m.PlatformAiPage,
                ),
              },
            ],
          },
        ],
      },

      // Logged in: an organisation's app (Tekansa staff only through "Log in as").
      {
        path: '/',
        element: (
          <RequireAuth>
            <RequireOrgApp>
              <AppLayout />
            </RequireOrgApp>
          </RequireAuth>
        ),
        children: [
          // Step 17c: the dashboard is /home; on connect.tekansa.com "/" is the website (nginx).
          { index: true, element: <Navigate to="/home" replace /> },
          {
            path: 'home',
            lazy: page(
              () => import('@/pages/app/home-page'),
              (m) => m.HomePage,
            ),
          },
          ...comingSoonRoutes,
          // Step 15: a deep link without access shows "No access".
          {
            element: <RequirePermission permission="campaigns.view" />,
            children: [
              {
                path: 'campaigns',
                lazy: page(
                  () => import('@/pages/campaigns/campaigns-page'),
                  (m) => m.CampaignsPage,
                ),
              },
              {
                path: 'campaigns/new',
                lazy: page(
                  () => import('@/pages/campaigns/new-campaign-page'),
                  (m) => m.NewCampaignPage,
                ),
              },
              {
                path: 'campaigns/:campaignId',
                lazy: page(
                  () => import('@/pages/campaigns/campaign-detail-page'),
                  (m) => m.CampaignDetailPage,
                ),
              },
              {
                path: 'campaigns/:campaignId/edit/:step',
                lazy: page(
                  () => import('@/pages/campaigns/wizard/wizard-page'),
                  (m) => m.CampaignWizardPage,
                ),
              },
            ],
          },
          // Step 15: a deep link without access shows "No access".
          {
            element: <RequirePermission permission="domains.view" />,
            children: [
              {
                path: 'domains',
                lazy: page(
                  () => import('@/pages/domains/domains-page'),
                  (m) => m.DomainsPage,
                ),
              },
              {
                path: 'domains/:domainId',
                lazy: page(
                  () => import('@/pages/domains/domain-detail-page'),
                  (m) => m.DomainDetailPage,
                ),
              },
            ],
          },
          // Step 15: a deep link without access shows "No access".
          {
            element: <RequirePermission permission="leads.view" />,
            children: [
              {
                path: 'leads',
                lazy: page(
                  () => import('@/pages/leads/leads-page'),
                  (m) => m.LeadsPage,
                ),
              },
              {
                path: 'leads/lists',
                lazy: page(
                  () => import('@/pages/leads/lists-page'),
                  (m) => m.ListsPage,
                ),
              },
              {
                path: 'leads/imports',
                lazy: page(
                  () => import('@/pages/leads/imports-page'),
                  (m) => m.ImportsPage,
                ),
              },
              {
                path: 'leads/suppression',
                lazy: page(
                  () => import('@/pages/leads/suppression/suppression-page'),
                  (m) => m.SuppressionPage,
                ),
              },
              {
                path: 'leads/import',
                lazy: page(
                  () => import('@/pages/leads/import/import-page'),
                  (m) => m.ImportPage,
                ),
              },
              {
                path: 'leads/imports/:importId',
                lazy: page(
                  () => import('@/pages/leads/import/import-page'),
                  (m) => m.ImportPage,
                ),
              },
            ],
          },
          // Step 15: a deep link without access shows "No access".
          {
            element: <RequirePermission permission="mailboxes.view" />,
            children: [
              {
                path: 'mailboxes',
                lazy: page(
                  () => import('@/pages/mailboxes/mailboxes-page'),
                  (m) => m.MailboxesPage,
                ),
              },
            ],
          },
          // Step 15: a deep link without access shows "No access".
          {
            element: <RequirePermission permission="inbox.view" />,
            children: [
              {
                path: 'inbox',
                lazy: page(
                  () => import('@/pages/inbox/inbox-page'),
                  (m) => m.InboxPage,
                ),
              },
            ],
          },
          // Step 16b: Reports (six reports in a left menu); the old Analytics links lead there.
          { path: 'analytics/*', element: <Navigate to="/reports" replace /> },
          {
            element: <RequirePermission permission="analytics.view" />,
            children: [
              {
                path: 'reports',
                lazy: page(
                  () => import('@/pages/reports/reports-layout'),
                  (m) => m.ReportsLayout,
                ),
                children: [
                  // Step 17: "Emailed and shared" moved to Settings → Reports.
                  {
                    path: 'emailed',
                    element: <Navigate to="/settings/reports/scheduled" replace />,
                  },
                  {
                    path: 'campaigns/:campaignId',
                    lazy: page(
                      () => import('@/pages/analytics/campaign-analytics-page'),
                      (m) => m.CampaignAnalyticsPage,
                    ),
                  },
                  {
                    path: ':report',
                    lazy: page(
                      () => import('@/pages/reports/report-page'),
                      (m) => m.ReportPage,
                    ),
                  },
                ],
              },
            ],
          },
          // Step 17b: opportunities live in the Inbox.
          {
            path: 'opportunities',
            element: <Navigate to="/inbox" replace />,
          },
          // Step 15: a deep link without access shows "No access".
          {
            element: <RequirePermission permission="deliverability.manage" />,
            children: [
              {
                path: 'sending',
                lazy: page(
                  () => import('@/pages/sending/sending-page'),
                  (m) => m.SendingPage,
                ),
              },
            ],
          },
          // Step 15: a deep link without access shows "No access".
          {
            element: <RequirePermission permission="deliverability.view" />,
            children: [
              {
                path: 'deliverability',
                lazy: page(
                  () => import('@/pages/deliverability/deliverability-page'),
                  (m) => m.DeliverabilityPage,
                ),
              },
            ],
          },
          // Step 17e: Tekansa staff's pages moved to Super Admin (/admin): old links forward.
          { path: 'platform/*', element: <PlatformRedirect /> },
          // Step 17: Settings with its own grouped, searchable menu; one page per setting.
          {
            path: 'settings',
            lazy: page(
              () => import('@/pages/settings/settings-layout'),
              (m) => m.SettingsLayout,
            ),
            children: [
              // Old URLs (before Step 17) open the new pages.
              ...Object.entries(OLD_SETTINGS_PATHS).map(([from, to]) => ({
                path: from,
                element: <Navigate to={`/settings/${to}`} replace />,
              })),
              {
                element: <RequirePermission permission="settings.view" />,
                children: [
                  {
                    path: 'organisation/profile',
                    lazy: page(
                      () => import('@/pages/settings/organisation-profile-page'),
                      (m) => m.OrganisationProfilePage,
                    ),
                  },
                ],
              },
              {
                element: <RequirePermission permission="users.view" />,
                children: [
                  {
                    path: 'organisation/users',
                    lazy: page(
                      () => import('@/pages/settings/users-page'),
                      (m) => m.UsersPage,
                    ),
                  },
                ],
              },
              {
                element: <RequirePermission permission="users.view" />,
                children: [
                  {
                    path: 'organisation/roles',
                    lazy: page(
                      () => import('@/pages/settings/roles-page'),
                      (m) => m.RolesPage,
                    ),
                  },
                ],
              },
              {
                element: <RequirePermission permission="settings.view" />,
                children: [
                  {
                    path: 'organisation/usage',
                    lazy: page(
                      () => import('@/pages/settings/usage-page'),
                      (m) => m.UsagePage,
                    ),
                  },
                ],
              },
              {
                element: <RequirePermission permission="users.manage" />,
                children: [
                  {
                    path: 'organisation/audit-log',
                    lazy: page(
                      () => import('@/pages/settings/audit-log-page'),
                      (m) => m.AuditLogPage,
                    ),
                  },
                ],
              },
              {
                path: 'account/profile',
                lazy: page(
                  () => import('@/pages/settings/profile-page'),
                  (m) => m.ProfilePage,
                ),
              },
              {
                path: 'account/security',
                lazy: page(
                  () => import('@/pages/settings/security-page'),
                  (m) => m.SecurityPage,
                ),
              },
              {
                path: 'account/notifications',
                lazy: page(
                  () => import('@/pages/settings/notifications-page'),
                  (m) => m.NotificationsPage,
                ),
              },
              {
                element: <RequirePermission permission="settings.view" />,
                children: [
                  {
                    path: 'sending/defaults',
                    lazy: page(
                      () => import('@/pages/settings/sending-defaults-page'),
                      (m) => m.SendingDefaultsPage,
                    ),
                  },
                ],
              },
              {
                element: <RequirePermission permission="settings.view" />,
                children: [
                  {
                    path: 'sending/verification',
                    lazy: page(
                      () => import('@/pages/settings/verification-page'),
                      (m) => m.VerificationSettingsPage,
                    ),
                  },
                ],
              },
              {
                element: <RequirePermission permission="leads.view" />,
                children: [
                  {
                    path: 'sending/suppression',
                    lazy: page(
                      () => import('@/pages/settings/suppression-settings-page'),
                      (m) => m.SuppressionSettingsPage,
                    ),
                  },
                ],
              },
              {
                element: <RequirePermission permission="inbox.manage" />,
                children: [
                  {
                    path: 'sending/replies',
                    lazy: page(
                      () => import('@/pages/settings/replies-page'),
                      (m) => m.RepliesSettingsPage,
                    ),
                  },
                ],
              },
              {
                element: <RequirePermission permission="analytics.export" />,
                children: [
                  {
                    path: 'reports/scheduled',
                    lazy: page(
                      () => import('@/pages/settings/scheduled-reports-page'),
                      (m) => m.ScheduledReportsPage,
                    ),
                  },
                ],
              },
              {
                element: <RequirePermission permission="analytics.export" />,
                children: [
                  {
                    path: 'reports/share-links',
                    lazy: page(
                      () => import('@/pages/settings/share-links-page'),
                      (m) => m.ShareLinksPage,
                    ),
                  },
                ],
              },
              {
                element: <RequirePermission permission="integrations.view" />,
                children: [
                  {
                    path: 'developers/api-keys',
                    lazy: page(
                      () => import('@/pages/settings/api-keys-page'),
                      (m) => m.ApiKeysPage,
                    ),
                  },
                ],
              },
              {
                element: <RequirePermission permission="integrations.edit" />,
                children: [
                  {
                    path: 'developers/webhooks',
                    lazy: page(
                      () => import('@/pages/settings/webhooks-page'),
                      (m) => m.WebhooksPage,
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        path: '*',
        lazy: page(
          () => import('@/pages/status/status-pages'),
          (m) => m.NotFoundPage,
        ),
      },
    ],
  },
];

export const createAppRouter = () => createBrowserRouter(routes);
